import { expectDefined } from '@utils';
import type { VisitableGraph } from '../interface';
import type { EID, NID, Nodes } from '../types';
import { ControlFlow } from './ControlFlow';

export interface DFSVisitor<G, N, E, O> {
  initNode?(graph: VisitableGraph<G, N, E>, node: NID): ControlFlow<O>;
  startNode?(graph: VisitableGraph<G, N, E>, node: NID): ControlFlow<O>;
  discoverNode?(graph: VisitableGraph<G, N, E>, node: NID): ControlFlow<O>;
  finishNode?(graph: VisitableGraph<G, N, E>, node: NID): ControlFlow<O>;

  examineEdge?(graph: VisitableGraph<G, N, E>, edge: EID): ControlFlow<O>;
  treeEdge?(graph: VisitableGraph<G, N, E>, edge: EID): ControlFlow<O>;
  backEdge?(graph: VisitableGraph<G, N, E>, edge: EID): ControlFlow<O>;
  forwardOrCrossEdge?(graph: VisitableGraph<G, N, E>, edge: EID): ControlFlow<O>;
  finishEdge?(graph: VisitableGraph<G, N, E>, edge: EID): ControlFlow<O>;

  finish?(graph: VisitableGraph<G, N, E>): O;
}

enum State {
  DISCOVERED = 'Discovered',
  FINISHED = 'Finished'
}

export interface DFSOpts {
  roots?: Nodes;
  rootsOnly?: boolean;
  excludedEdge?: EID;
}

export function depthFirstSearch<G, N, E, O>(
  graph: VisitableGraph<G, N, E>,
  visitor: DFSVisitor<G, N, E, O>,
  opts?: DFSOpts
): O {
  if (visitor.initNode !== undefined) {
    for (const node of graph.nodes) {
      const result = visitor.initNode(graph, node);
      if (result.isBreak) {
        return result.value;
      }
    }
  }

  const states = new Map<NID, State>();

  const excludedEdge = opts?.excludedEdge;
  for (const root of opts?.roots ?? new Set()) {
    const result = searchFromRoot(graph, visitor, states, root, excludedEdge);
    if (result.isBreak) {
      return result.value;
    }
  }

  if (!opts?.rootsOnly) {
    for (const node of graph.nodes) {
      const result = searchFromRoot(graph, visitor, states, node, excludedEdge);
      if (result.isBreak) {
        return result.value;
      }
    }
  }

  if (visitor.finish !== undefined) {
    return visitor.finish(graph);
  } else {
    return undefined as unknown as O;
  }
}

function searchFromRoot<G, N, E, O>(
  graph: VisitableGraph<G, N, E>,
  visitor: DFSVisitor<G, N, E, O>,
  states: Map<NID, State>,
  root: NID,
  excludedEdge: EID | undefined
): ControlFlow<O> {
  if (states.has(root)) {
    return ControlFlow.continue();
  }

  let result: ControlFlow<O>;

  if (visitor.startNode !== undefined) {
    result = visitor.startNode(graph, root);
    if (result.isBreak) {
      return result;
    }
  }

  states.set(root, State.DISCOVERED);

  if (visitor.discoverNode !== undefined) {
    result = visitor.discoverNode(graph, root);
    if (result.isBreak) {
      return result;
    }
  }

  let outEdges = Array.from(graph.outEdges(root)).sort();
  if (excludedEdge !== undefined) {
    outEdges = outEdges.filter(edge => edge !== excludedEdge);
  }

  // const stack: [NID, EID | undefined, EID[]][] = [[root, undefined, outEdges]];
  const stack: { source: NID; finishedEdge?: EID; remainingOutEdges: EID[] }[] = [
    { source: root, remainingOutEdges: outEdges }
  ];

  while (stack.length > 0) {
    const stackEntry = expectDefined(stack.pop());

    let { source, remainingOutEdges } = stackEntry;
    const { finishedEdge } = stackEntry;

    if (finishedEdge !== undefined) {
      if (visitor.finishEdge !== undefined) {
        result = visitor.finishEdge(graph, finishedEdge);
        if (result.isBreak) {
          return result;
        }
      }
    }

    while (remainingOutEdges.length > 0) {
      const edge = expectDefined(remainingOutEdges.pop());
      const target = graph.target(edge);

      if (visitor.examineEdge !== undefined) {
        result = visitor.examineEdge(graph, edge);
        if (result.isBreak) {
          return result;
        }
      }

      const targetState = states.get(target);
      if (targetState === undefined) {
        if (visitor.treeEdge !== undefined) {
          result = visitor.treeEdge(graph, edge);
          if (result.isBreak) {
            return result;
          }
        }

        stack.push({
          source,
          finishedEdge: edge,
          remainingOutEdges: [...remainingOutEdges]
        });
        source = target;
        states.set(source, State.DISCOVERED);

        if (visitor.discoverNode !== undefined) {
          result = visitor.discoverNode(graph, source);
          if (result.isBreak) {
            return result;
          }
        }

        remainingOutEdges = Array.from(graph.outEdges(source)).sort();
      } else if (targetState === State.DISCOVERED) {
        if (visitor.backEdge !== undefined) {
          result = visitor.backEdge(graph, edge);
          if (result.isBreak) {
            return result;
          }
        }

        if (visitor.finishEdge !== undefined) {
          result = visitor.finishEdge(graph, edge);
          if (result.isBreak) {
            return result;
          }
        }
      } else {
        if (visitor.forwardOrCrossEdge !== undefined) {
          result = visitor.forwardOrCrossEdge(graph, edge);
          if (result.isBreak) {
            return result;
          }
        }

        if (visitor.finishEdge !== undefined) {
          result = visitor.finishEdge(graph, edge);
          if (result.isBreak) {
            return result;
          }
        }
      }
    }

    states.set(source, State.FINISHED);

    if (visitor.finishNode !== undefined) {
      result = visitor.finishNode(graph, source);
      if (result.isBreak) {
        return result;
      }
    }
  }

  return ControlFlow.continue();
}
